import {
  acceptInvitationResponseSchema,
  assignmentSettingsResponseSchema,
  reportGoalsResponseSchema,
  callSequenceSettingsResponseSchema,
  cancelInvitationResponseSchema,
  createOrganizationResponseSchema,
  healthResponseSchema,
  homeTodayResponseSchema,
  invitationIssuedResponseSchema,
  invitationPreviewSchema,
  membershipResponseSchema,
  meResponseSchema,
  myContextResponseSchema,
  myMembershipsResponseSchema,
  observablePeopleResponseSchema,
  organizationSettingsResponseSchema,
  permissionCatalogResponseSchema,
  permissionCatalogUpdateResponseSchema,
  platformOrganizationResponseSchema,
  platformOrganizationsResponseSchema,
  roleResponseSchema,
  rolesResponseSchema,
  teamResponseSchema,
  telephonyConnectionResponseSchema,
  telephonyConnectionViewSchema,
  verifyCallerResponseSchema,
  type AcceptInvitationBody,
  type AcceptInvitationResponse,
  type CreateInvitationBody,
  type CreateOrganizationBody,
  type CreateRoleBody,
  type EnableTelephonyBody,
  type InvitationPreview,
  type Me,
  type SaveAssignmentSettingsBody,
  type SaveReportGoalsBody,
  type SaveCallSequenceSettingsBody,
  type TelephonyConnectionView,
  type TelephonySettingsBody,
  type UpdateMeBody,
  type UpdateMembershipBody,
  type UpdateOrganizationSettingsBody,
  type UpdateRoleBody,
} from "@gioia365/contracts";
import { MutationCache, QueryCache, QueryClient, queryOptions, useMutation, useQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { useRouteContext } from "@tanstack/react-router";
import { commercialViewOf, tabKey, type TabContext } from "../session/tab-context";
import { reportKeys } from "../reports/report-keys";
import { callerNumbersKey } from "../telephony/caller-number-queries";
import { whatsAppSettingsKeys } from "../whatsapp/settings-keys";
import { ApiRequestError, apiRequest } from "./http";

// Le query dell'applicazione, tipizzate con i contratti. Chiavi (piano M1b D2):
// - `["me", ...]`: l'identità della sessione, uguale in ogni azienda;
// - `["scheda", tabKey(tab), ...]`: i dati dell'azienda della scheda, tolti tutti insieme al cambio di azienda;
// - `["piattaforma", ...]`: i dati del Super admin di Gioia365 fuori da ogni azienda;
// - `["health"]`: lo stato pubblico del server, uguale per tutti.
// Le query delle guardie non riprovano da sole: la pagina d'errore offre «Riprova» subito.

/** Un guasto passeggero (rete, server che non risponde) merita un nuovo tentativo; un rifiuto no. */
export function isTransientError(error: unknown): boolean {
  return error instanceof ApiRequestError && (error.code === "network" || error.status >= 500);
}

/**
 * La cache dell'applicazione: due tentativi in più soltanto per i guasti passeggeri (i test li spengono con
 * `retryTransient: false`). `onQueryError` riceve ogni errore di una query, anche di quelle rilette in
 * background dopo il primo caricamento: il router lo usa per applicare D17 anche lì (`handleQueryError`).
 * `onMutationError` riceve ogni errore di una scrittura: il router chiude una Vista Commerciale rifiutata
 * (`handleMutationError`), che la scrittura stessa spiega già dove è partita.
 */
export function createQueryClient(
  options: { onQueryError?: (error: unknown, queryKey: QueryKey) => void; onMutationError?: (error: unknown) => void; retryTransient?: boolean } = {},
): QueryClient {
  const retryTransient = options.retryTransient ?? true;
  return new QueryClient({
    queryCache: new QueryCache({ onError: (error, query) => options.onQueryError?.(error, query.queryKey) }),
    mutationCache: new MutationCache({ onError: (error) => options.onMutationError?.(error) }),
    defaultOptions: { queries: { retry: (failures, error) => retryTransient && failures < 2 && isTransientError(error) } },
  });
}

/**
 * Lo stato pubblico del server: versione e se le email di sistema partono (`emailDelivery`, D10), così
 * «Password dimenticata?» non promette un'email che non arriva.
 */
export const healthQuery = queryOptions({
  queryKey: ["health"],
  queryFn: ({ signal }) => apiRequest("GET", "/api/health", { schema: healthResponseSchema, signal }),
  // Cambia soltanto con un nuovo avvio del server.
  staleTime: 5 * 60_000,
  retry: false,
});

/** L'identità della sessione; `null` senza sessione (401) o sul server senza autenticazione (404, D3). */
export const meQuery = queryOptions({
  queryKey: ["me"],
  queryFn: async ({ signal }): Promise<Me | null> => {
    try {
      const response = await apiRequest("GET", "/api/me", { schema: meResponseSchema, signal });
      return response.user;
    } catch (error) {
      if (error instanceof ApiRequestError && (error.status === 401 || error.status === 404)) return null;
      throw error;
    }
  },
  // Cambia soltanto con l'accesso, l'uscita e le modifiche dell'account, che aggiornano la cache da sé.
  staleTime: Infinity,
  retry: false,
});

/** Le aziende attive di cui la persona fa parte (fuori da ogni azienda: nessun header). */
export const myMembershipsQuery = queryOptions({
  queryKey: ["me", "memberships"],
  queryFn: async ({ signal }) => (await apiRequest("GET", "/api/me/memberships", { schema: myMembershipsResponseSchema, signal })).memberships,
  // La guardia e la pagina «Scegli l'azienda» la leggono una dopo l'altra: una sola richiesta.
  staleTime: 30_000,
  retry: false,
});

/**
 * Azienda, ruolo, permessi e impostazioni che il server concede alla scheda (D4). Con «Guarda come…» (la Vista
 * Commerciale) anche i permessi della persona osservata, da cui si costruisce il menu: la chiave porta la vista, così un
 * contesto letto con un'altra vista (o senza) non diventa mai quello della vista di adesso.
 */
export function contextQuery(tab: TabContext) {
  return queryOptions({
    queryKey: ["scheda", tabKey(tab), "context", commercialViewOf(tab)],
    queryFn: async ({ signal }) => (await apiRequest("GET", "/api/context", { schema: myContextResponseSchema, tab, signal })).context,
    // Cambia di rado; il server controlla comunque ogni permesso a ogni richiesta.
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * Le persone che chi guarda può scegliere in «Guarda come…» (decisione del product owner del 2 ottobre 2026), nell'ordine
 * dei gruppi: le decide il server con la stessa regola dell'header della vista, così il selettore non offre mai chi il
 * server rifiuterebbe. Si rilegge a ogni apertura del selettore.
 */
export function observablePeopleQuery(tab: TabContext) {
  return queryOptions({
    queryKey: ["scheda", tabKey(tab), "commercial-view", "people"],
    queryFn: async ({ signal }) => (await apiRequest("GET", "/api/commercial-view/people", { schema: observablePeopleResponseSchema, tab, signal })).people,
  });
}

export function teamQuery(tab: TabContext) {
  return queryOptions({
    queryKey: ["scheda", tabKey(tab), "team"],
    queryFn: async ({ signal }) => (await apiRequest("GET", "/api/team", { schema: teamResponseSchema, tab, signal })).team,
  });
}

/** Ruoli dell'azienda e catalogo di ciò che la schermata «Ruoli e permessi» può offrire. */
export function rolesQuery(tab: TabContext) {
  return queryOptions({
    queryKey: ["scheda", tabKey(tab), "roles"],
    queryFn: ({ signal }) => apiRequest("GET", "/api/roles", { schema: rolesResponseSchema, tab, signal }),
  });
}

export function settingsQuery(tab: TabContext) {
  return queryOptions({
    queryKey: ["scheda", tabKey(tab), "settings"],
    queryFn: async ({ signal }) =>
      (await apiRequest("GET", "/api/organization/settings", { schema: organizationSettingsResponseSchema, tab, signal })).settings,
  });
}

/**
 * Salva le impostazioni dell'azienda (`PATCH /api/organization/settings`, piano M1b Task 10): il corpo porta
 * soltanto ciò che cambia, e `workingCalendar` sostituisce il calendario intero. La risposta prende subito il
 * posto della copia in cache; la rilettura conferma anche il contesto della scheda, che porta il fuso e la
 * lingua dell'azienda usati altrove per le date, e si fa anche dopo un rifiuto.
 */
export function useUpdateSettings(tab: TabContext) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateOrganizationSettingsBody) =>
      apiRequest("PATCH", "/api/organization/settings", { schema: organizationSettingsResponseSchema, body, tab }),
    onSuccess: ({ settings }) => queryClient.setQueryData(settingsQuery(tab).queryKey, settings),
    onSettled: () => {
      // «Sequenza di chiamate» e «Assegnazione dei Lead» hanno la stessa versione delle impostazioni, e l'esempio della prima segue il calendario.
      for (const queryKey of [settingsQuery(tab).queryKey, contextQuery(tab).queryKey, callSequenceSettingsQuery(tab).queryKey, assignmentSettingsQuery(tab).queryKey]) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}

/**
 * La regola della sequenza di chiamate dell'azienda (`GET /api/settings/call-sequence`, piano M5, Task 13): la regola, la versione
 * delle impostazioni, se chi chiede la può cambiare e l'esempio di un Lead che entra adesso.
 */
export function callSequenceSettingsQuery(tab: TabContext) {
  return queryOptions({
    queryKey: ["scheda", tabKey(tab), "call-sequence"],
    queryFn: ({ signal }) => apiRequest("GET", "/api/settings/call-sequence", { schema: callSequenceSettingsResponseSchema, tab, signal }),
  });
}

/**
 * Salva la regola (`PUT /api/settings/call-sequence`): la risposta prende il posto della copia in cache. La versione è quella delle
 * impostazioni, quindi anche le impostazioni si rileggono; dopo un rifiuto (per esempio `409 version_conflict`) si rilegge la regola.
 */
export function useSaveCallSequenceSettings(tab: TabContext) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SaveCallSequenceSettingsBody) => apiRequest("PUT", "/api/settings/call-sequence", { schema: callSequenceSettingsResponseSchema, body, tab }),
    onSuccess: (response) => queryClient.setQueryData(callSequenceSettingsQuery(tab).queryKey, response),
    onError: () => void queryClient.invalidateQueries({ queryKey: callSequenceSettingsQuery(tab).queryKey }),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: settingsQuery(tab).queryKey }),
  });
}

/**
 * La regola di assegnazione dei Lead dell'azienda (`GET /api/settings/assignment`, piano M6, Task 13): la regola, la versione delle
 * impostazioni, se chi chiede la può cambiare, chi può entrare nel turno, l'ultimo servito e «I prossimi 6 Lead nuovi». La legge
 * anche «Nuovo Lead», per dire che cosa fa la scelta vuota dell'owner.
 */
export function assignmentSettingsQuery(tab: TabContext) {
  return queryOptions({
    queryKey: ["scheda", tabKey(tab), "assignment"],
    queryFn: ({ signal }) => apiRequest("GET", "/api/settings/assignment", { schema: assignmentSettingsResponseSchema, tab, signal }),
  });
}

/**
 * Salva la regola (`PUT /api/settings/assignment`): la risposta prende il posto della copia in cache. La versione è quella delle
 * impostazioni, quindi anche le impostazioni si rileggono; dopo un rifiuto (per esempio `409 version_conflict`) si rilegge la regola.
 */
export function useSaveAssignmentSettings(tab: TabContext) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SaveAssignmentSettingsBody) => apiRequest("PUT", "/api/settings/assignment", { schema: assignmentSettingsResponseSchema, body, tab }),
    onSuccess: (response) => queryClient.setQueryData(assignmentSettingsQuery(tab).queryKey, response),
    onError: () => void queryClient.invalidateQueries({ queryKey: assignmentSettingsQuery(tab).queryKey }),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: settingsQuery(tab).queryKey }),
  });
}

/**
 * Gli obiettivi del Report di un mese (`GET /api/settings/report-goals`, piano M7, Task 6): la versione, il valore medio, le percentuali
 * e una riga per Commerciale con l'obiettivo del mese. La legge soltanto chi ha la sezione delle Impostazioni.
 */
export function reportGoalsSettingsQuery(tab: TabContext, month: string) {
  return queryOptions({
    queryKey: ["scheda", tabKey(tab), "report-goals", month],
    queryFn: ({ signal }) => apiRequest("GET", `/api/settings/report-goals?month=${encodeURIComponent(month)}`, { schema: reportGoalsResponseSchema, tab, signal }),
  });
}

/**
 * Salva gli obiettivi (`PUT /api/settings/report-goals`): la risposta prende il posto della copia del mese, e il Report si rilegge, perché
 * colori e «Obiettivi del mese» vengono da qui; dopo un rifiuto (per esempio `409 version_conflict`) si rileggono gli obiettivi di ogni
 * mese, che hanno la stessa versione.
 */
export function useSaveReportGoals(tab: TabContext) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SaveReportGoalsBody) => apiRequest("PUT", "/api/settings/report-goals", { schema: reportGoalsResponseSchema, body, tab }),
    onSuccess: (response) => {
      queryClient.setQueryData(reportGoalsSettingsQuery(tab, response.month).queryKey, response);
      void queryClient.invalidateQueries({ queryKey: ["scheda", tabKey(tab), "report-goals"], predicate: (query) => query.queryKey[3] !== response.month });
    },
    onError: () => void queryClient.invalidateQueries({ queryKey: ["scheda", tabKey(tab), "report-goals"] }),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: reportKeys.all(tabKey(tab)) }),
  });
}

/**
 * La Home «Oggi» e «Sequenze rispettate oggi» (`GET /api/today`, piano M5, Task 22): i numeri del giorno dell'azienda. Dipendono
 * da chi si guarda, quindi la chiave porta la «Guarda come…»; si rileggono a ogni ritorno sulla Home (nessun `staleTime`).
 */
export function todayQuery(tab: TabContext) {
  return queryOptions({
    queryKey: ["scheda", tabKey(tab), "today", commercialViewOf(tab)],
    queryFn: ({ signal }) => apiRequest("GET", "/api/today", { schema: homeTodayResponseSchema, tab, signal }),
    // Lo stato delle disco e demo di oggi («Da fare», «In corso», «Da chiudere») lo dice il server all'istante: si rilegge ogni minuto,
    // come il banner dei Task, finché la Home è aperta.
    refetchInterval: 60_000,
  });
}

/**
 * Le aziende di Gioia365 e se in questo ambiente i numeri ammessi dei WhatsApp automatici sono richiesti
 * (`whatsappAllowlistRequired`, piano M6, R2, Task 7b): la pagina delle aziende mostra allora «Numeri WhatsApp».
 */
export const platformOrganizationsQuery = queryOptions({
  queryKey: ["piattaforma", "aziende"],
  queryFn: ({ signal }) => apiRequest("GET", "/api/platform/organizations", { schema: platformOrganizationsResponseSchema, signal }),
});

// Aziende di Gioia365 (piano M1b Task 11): rotte di piattaforma, senza header dell'azienda. Ogni modifica, riuscita
// o rifiutata, fa rileggere l'elenco: dopo una creazione fermata a metà l'azienda compare «in preparazione», e dopo
// un `409 invalid_state` la tabella mostra lo stato che il server ha davvero. La rilettura non si aspetta.

function useOrganizationsRefresh() {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: platformOrganizationsQuery.queryKey });
}

/**
 * Crea un'azienda, o riprende quella rimasta «in preparazione» (stesso indirizzo breve: il server usa i valori
 * salvati alla prima chiamata e ignora il resto del corpo). Può durare: crea anche il database dell'azienda.
 */
export function useCreateOrganization() {
  const refresh = useOrganizationsRefresh();
  return useMutation({
    mutationFn: (body: CreateOrganizationBody) =>
      apiRequest("POST", "/api/platform/organizations", { schema: createOrganizationResponseSchema, body }),
    onSettled: refresh,
  });
}

export type OrganizationTransition = "suspend" | "resume" | "close";

/** Sospende, riattiva o chiude un'azienda. */
export function useOrganizationTransition() {
  const refresh = useOrganizationsRefresh();
  return useMutation({
    mutationFn: ({ organizationId, action }: { organizationId: string; action: OrganizationTransition }) =>
      apiRequest("POST", `/api/platform/organizations/${encodeURIComponent(organizationId)}/${action}`, { schema: platformOrganizationResponseSchema }),
    onSettled: refresh,
  });
}

/**
 * «Cambia nome» di un'azienda (decisione del 4 ottobre 2026). Oltre all'elenco si rileggono le aziende della persona e
 * il contesto delle schede, perché il menu delle aziende e il guscio mostrano il nome.
 */
export function useRenameOrganization() {
  const queryClient = useQueryClient();
  const refresh = useOrganizationsRefresh();
  return useMutation({
    mutationFn: ({ organizationId, name }: { organizationId: string; name: string }) =>
      apiRequest("PATCH", `/api/platform/organizations/${encodeURIComponent(organizationId)}`, { schema: platformOrganizationResponseSchema, body: { name } }),
    onSettled: () => {
      refresh();
      void queryClient.invalidateQueries({ queryKey: myMembershipsQuery.queryKey });
      void queryClient.invalidateQueries({ queryKey: ["scheda"] });
    },
  });
}

// «Telefonia» di un'azienda (piano M4, Task 14, rotte del Task 5): del Super admin di Gioia365, senza header dell'azienda.
// La chiave non sta sotto quella dell'elenco delle aziende (`["piattaforma", "aziende"]`): una rilettura dell'elenco non
// rilegge la telefonia. Ogni rotta risponde con la vista della connessione (`null` se la telefonia non è mai stata accesa) e
// non contiene mai un segreto. Un passo del provisioning che fallisce risponde `502 telephony_provisioning_failed` con la
// vista nei dettagli: anche quella entra nella cache.

/** Ogni quanto si rilegge la telefonia mentre il fornitore si imposta o una verifica è in corso (Task 14). */
export const TELEPHONY_REFRESH_MS = 3_000;

const telephonyPath = (organizationId: string) => `/api/platform/organizations/${encodeURIComponent(organizationId)}/telephony`;

export function platformTelephonyQuery(organizationId: string) {
  return queryOptions({
    queryKey: ["piattaforma", "telefonia", organizationId],
    queryFn: async ({ signal }) =>
      (await apiRequest("GET", telephonyPath(organizationId), { schema: telephonyConnectionResponseSchema, signal })).telephony,
  });
}

/** La vista che un rifiuto `telephony_provisioning_failed` porta nei dettagli, se c'è e ha la forma giusta. */
function telephonyViewOfError(error: unknown): TelephonyConnectionView | null {
  if (!(error instanceof ApiRequestError) || error.code !== "telephony_provisioning_failed") return null;
  const parsed = telephonyConnectionViewSchema.safeParse(error.details?.telephony);
  return parsed.success ? parsed.data : null;
}

/**
 * Una scrittura sulla telefonia di un'azienda. Una alla volta per azienda, nell'ordine dei clic. La vista della risposta
 * (o del rifiuto del provisioning) prende subito il posto di quella in cache, dopo aver annullato la rilettura in corso: una
 * rilettura partita prima, e arrivata dopo, mostrerebbe un passo vecchio. Riuscita o rifiutata, la vista si rilegge, così
 * il pannello mostra ciò che il server ha davvero (anche dopo un `409 version_conflict`); la rilettura non si aspetta.
 * `gcTime` è quanto la cache delle scritture tiene la risposta di una scrittura che nessuno osserva più (di norma 5 minuti): chi
 * porta nella risposta qualcosa che non deve restare in memoria lo mette a 0.
 */
function useTelephonyWrite<Variables, Answer>(
  organizationId: string,
  options: { request: (variables: Variables) => Promise<Answer>; viewOf: (answer: Answer) => TelephonyConnectionView | null; gcTime?: number },
) {
  const queryClient = useQueryClient();
  const queryKey = platformTelephonyQuery(organizationId).queryKey;
  async function show(view: TelephonyConnectionView | null) {
    if (view === null) return;
    await queryClient.cancelQueries({ queryKey });
    queryClient.setQueryData(queryKey, view);
  }
  return useMutation({
    mutationFn: options.request,
    scope: { id: `telefonia-${organizationId}` },
    ...(options.gcTime === undefined ? {} : { gcTime: options.gcTime }),
    onSuccess: (answer) => show(options.viewOf(answer)),
    onError: (error) => show(telephonyViewOfError(error)),
    onSettled: () => void queryClient.invalidateQueries({ queryKey }),
  });
}

/** «Accendi la telefonia»: il fornitore e, per un'azienda `prova-`, i numeri ammessi. Dura quanto la saga del server. */
export function useEnableTelephony(organizationId: string) {
  return useTelephonyWrite(organizationId, {
    request: (body: EnableTelephonyBody) =>
      apiRequest("POST", `${telephonyPath(organizationId)}/enable`, { schema: telephonyConnectionResponseSchema, body }),
    viewOf: (response) => response.telephony,
  });
}

/** «Riprova»: il server riprende dal passo fermo. */
export function useProvisionTelephony(organizationId: string) {
  return useTelephonyWrite(organizationId, {
    request: () => apiRequest("POST", `${telephonyPath(organizationId)}/provision`, { schema: telephonyConnectionResponseSchema }),
    viewOf: (response) => response.telephony,
  });
}

/** Registrazione, avviso, scadenza dell'audio e numeri ammessi, con la versione letta (`409 version_conflict` se è cambiata). */
export function useSaveTelephonySettings(organizationId: string) {
  return useTelephonyWrite(organizationId, {
    request: (body: TelephonySettingsBody) =>
      apiRequest("PATCH", telephonyPath(organizationId), { schema: telephonyConnectionResponseSchema, body }),
    viewOf: (response) => response.telephony,
  });
}

/**
 * Chiede al fornitore di verificare il numero mostrato; la risposta ha il codice da digitare, e lo stato si rilegge. Il codice
 * vale per chi tiene il telefono e basta: la cache delle scritture non lo tiene i soliti 5 minuti (`gcTime: 0`), e chi chiede
 * la verifica lo copia nel suo stato e subito dopo chiama `reset()`, così nella cache non resta (`CallerSection`).
 */
export function useVerifyCaller(organizationId: string) {
  return useTelephonyWrite(organizationId, {
    request: (body: { e164: string }) =>
      apiRequest("POST", `${telephonyPath(organizationId)}/caller/verify`, { schema: verifyCallerResponseSchema, body }),
    viewOf: () => null,
    gcTime: 0,
  });
}

/** «Prova la connessione»: soltanto lettura presso il fornitore; il server scrive l'ora e il codice dell'esito. */
export function useCheckTelephony(organizationId: string) {
  return useTelephonyWrite(organizationId, {
    request: () => apiRequest("POST", `${telephonyPath(organizationId)}/check`, { schema: telephonyConnectionResponseSchema }),
    viewOf: (response) => response.telephony,
  });
}

/** «Spegni la telefonia»: il fornitore resta com'è. */
export function useDisableTelephony(organizationId: string) {
  return useTelephonyWrite(organizationId, {
    request: () => apiRequest("POST", `${telephonyPath(organizationId)}/disable`, { schema: telephonyConnectionResponseSchema }),
    viewOf: (response) => response.telephony,
  });
}

export const permissionCatalogQuery = queryOptions({
  queryKey: ["piattaforma", "catalogo"],
  queryFn: async ({ signal }) =>
    (await apiRequest("GET", "/api/platform/permission-catalog", { schema: permissionCatalogResponseSchema, signal })).catalog,
});

/** L'azienda della scheda, convalidata dalla guardia della rotta `azienda`. */
export function useTab(): TabContext {
  return useRouteContext({ from: "/autenticato/guscio/azienda", select: (context) => context.tab });
}

/** L'identità della sessione nelle pagine protette: la guardia l'ha già letta, la cache la tiene aggiornata. */
export function useMe(): Me {
  const guarded = useRouteContext({ from: "/autenticato", select: (context) => context.me });
  const { data } = useQuery(meQuery);
  return data ?? guarded;
}

/**
 * Modifica l'identità della sessione (`PATCH /api/me`): nome e cognome dell'account e lingua, soltanto `"it"` o
 * `"en"` (una scelta non si toglie: decisione 39 del 30 settembre 2026). La risposta aggiorna `["me"]`, e con essa
 * la lingua dell'interfaccia (`LocaleSync`).
 */
export function useUpdateMe() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateMeBody) => apiRequest("PATCH", "/api/me", { schema: meResponseSchema, body }),
    // Una modifica alla volta, nell'ordine dei clic: l'ultima risposta è l'ultima scelta della persona.
    scope: { id: "me" },
    onSuccess: (response) => queryClient.setQueryData(meQuery.queryKey, response.user),
  });
}

// Team (piano M1b Task 7): ogni modifica passa dall'header dell'azienda della scheda e, riuscita o rifiutata,
// fa rileggere il team, così la tabella mostra sempre ciò che il server ha davvero (anche dopo un
// `409 invalid_state`, quando qualcun altro l'ha appena cambiato). La rilettura non si aspetta: la finestra si
// chiude e l'avviso arriva subito.

function useTeamRefresh(tab: TabContext) {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: teamQuery(tab).queryKey });
}

/** Invita una persona; `manualDelivery.link` quando il server non ha mandato l'email. */
export function useInvite(tab: TabContext) {
  const refresh = useTeamRefresh(tab);
  return useMutation({
    mutationFn: (body: CreateInvitationBody) => apiRequest("POST", "/api/invitations", { schema: invitationIssuedResponseSchema, body, tab }),
    onSettled: refresh,
  });
}

/** Manda di nuovo un invito: il server annulla il vecchio e ne crea uno nuovo, con un id nuovo. */
export function useResendInvitation(tab: TabContext) {
  const refresh = useTeamRefresh(tab);
  return useMutation({
    mutationFn: (invitationId: string) =>
      apiRequest("POST", `/api/invitations/${encodeURIComponent(invitationId)}/resend`, { schema: invitationIssuedResponseSchema, tab }),
    onSettled: refresh,
  });
}

export function useCancelInvitation(tab: TabContext) {
  const refresh = useTeamRefresh(tab);
  return useMutation({
    mutationFn: (invitationId: string) =>
      apiRequest("POST", `/api/invitations/${encodeURIComponent(invitationId)}/cancel`, { schema: cancelInvitationResponseSchema, tab }),
    onSettled: refresh,
  });
}

/**
 * Nome nell'azienda e ruolo di una persona: il corpo porta soltanto ciò che cambia. Il nome e il ruolo cambiano anche la riga del
 * WhatsApp della persona (M6, R1: il nome, e a chi si può impostare), che si rilegge con il team.
 */
export function useUpdateMembership(tab: TabContext) {
  const refresh = useTeamRefresh(tab);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ membershipId, body }: { membershipId: string; body: UpdateMembershipBody }) =>
      apiRequest("PATCH", `/api/memberships/${encodeURIComponent(membershipId)}`, { schema: membershipResponseSchema, body, tab }),
    onSettled: () => {
      refresh();
      void queryClient.invalidateQueries({ queryKey: whatsAppSettingsKeys.senders(tab) });
    },
  });
}

export type MembershipTransition = "suspend" | "resume" | "archive";

/**
 * Sospende, riattiva o archivia una persona. Cambia anche chi può essere owner e quanti Lead restano da riassegnare
 * (M2): si rileggono pure le letture `lead-owners` della scheda, anche dopo un rifiuto (`membership_has_leads`). E il parco
 * dei cellulari (M4 Task 17): l'archiviazione definitiva libera il cellulare della persona. E il WhatsApp delle persone (M6, R1):
 * chi è sospeso o archiviato cambia riga, e il suo numero diventa libero.
 */
export function useMembershipTransition(tab: TabContext) {
  const refresh = useTeamRefresh(tab);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ membershipId, action }: { membershipId: string; action: MembershipTransition }) =>
      apiRequest("POST", `/api/memberships/${encodeURIComponent(membershipId)}/${action}`, { schema: membershipResponseSchema, tab }),
    onSettled: () => {
      refresh();
      void queryClient.invalidateQueries({ queryKey: ["scheda", tabKey(tab), "lead-owners"] });
      void queryClient.invalidateQueries({ queryKey: callerNumbersKey(tab) });
      void queryClient.invalidateQueries({ queryKey: whatsAppSettingsKeys.senders(tab) });
    },
  });
}

// Ruoli (piano M1b Task 9). Ogni richiesta porta l'azienda della scheda; chi le usa rilegge ruoli, team e
// contesto quando serve (`useRolesRefresh`): un ruolo cambiato può cambiare anche i poteri di chi guarda.

/** Rilegge ruoli, team e contesto della scheda: conteggi per ruolo, nomi nel team, poteri di chi guarda. */
export function useRolesRefresh(tab: TabContext) {
  const queryClient = useQueryClient();
  return () => {
    for (const queryKey of [rolesQuery(tab).queryKey, teamQuery(tab).queryKey, contextQuery(tab).queryKey]) {
      void queryClient.invalidateQueries({ queryKey });
    }
  };
}

/** Un ruolo nuovo, copiato da `copyFromRoleId` (livello, permessi e funzioni vengono da lì). */
export function useCreateRole(tab: TabContext) {
  return useMutation({
    mutationFn: (body: CreateRoleBody) => apiRequest("POST", "/api/roles", { schema: roleResponseSchema, body, tab }),
  });
}

/** Nome, descrizione, matrice e funzioni: `areaPermissions` e `features` sostituiscono quelli salvati. */
export function useUpdateRole(tab: TabContext) {
  return useMutation({
    mutationFn: ({ roleId, body }: { roleId: string; body: UpdateRoleBody }) =>
      apiRequest("PATCH", `/api/roles/${encodeURIComponent(roleId)}`, { schema: roleResponseSchema, body, tab }),
  });
}

/** «Elimina»: il server archivia il ruolo, se nessuno lo ha, e il nome torna libero. */
export function useArchiveRole(tab: TabContext) {
  return useMutation({
    mutationFn: (roleId: string) => apiRequest("DELETE", `/api/roles/${encodeURIComponent(roleId)}`, { schema: roleResponseSchema, tab }),
  });
}

/**
 * Il Super admin di Gioia365 accende o spegne una funzione per tutte le aziende (nessun header: vale ovunque).
 * Spegnerla la toglie dai ruoli non di sistema di ogni azienda: si rileggono anche i dati delle aziende in cache.
 */
export function useUpdateCatalogEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ key, availableToOrganizations }: { key: string; availableToOrganizations: boolean }) =>
      apiRequest("PATCH", `/api/platform/permission-catalog/${encodeURIComponent(key)}`, {
        schema: permissionCatalogUpdateResponseSchema,
        body: { availableToOrganizations },
      }),
    // Una modifica alla volta, nell'ordine dei clic.
    scope: { id: "catalogo" },
    onSuccess: ({ entry }) => {
      queryClient.setQueryData(permissionCatalogQuery.queryKey, (catalog) => catalog?.map((current) => (current.key === entry.key ? entry : current)));
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: permissionCatalogQuery.queryKey });
      void queryClient.invalidateQueries({ queryKey: ["scheda"] });
    },
  });
}

// Inviti (rotte pubbliche, senza header dell'azienda): il token dell'invito viaggia soltanto nel corpo della
// richiesta, mai in un percorso o in una chiave di cache (piano M1a D3, M1b Task 6).

/** Ciò che l'invito dice di sé, con l'email mascherata: serve a scegliere quale modulo mostrare. */
export function previewInvitation(token: string, signal?: AbortSignal): Promise<InvitationPreview> {
  return apiRequest("POST", "/api/invitations/preview", {
    schema: invitationPreviewSchema,
    body: { token },
    ...(signal ? { signal } : {}),
  });
}

/** Accetta l'invito; se il server apre una sessione, il cookie arriva con la risposta. */
export function acceptInvitation(body: AcceptInvitationBody): Promise<AcceptInvitationResponse> {
  return apiRequest("POST", "/api/invitations/accept", { schema: acceptInvitationResponseSchema, body });
}
